import { useState, useEffect, useCallback } from 'react';

const STREAMING_PREFERENCE_KEY = 'unichat-streaming-preference';

export interface StreamingPreference {
  enabled: boolean;
  lastUpdated: number;
}

export const useStreamingPreference = () => {
  const [isStreamingEnabled, setIsStreamingEnabled] = useState<boolean>(true); // Default to enabled
  const [isLoading, setIsLoading] = useState(true);

  // Load preference from localStorage on mount
  useEffect(() => {
    const loadPreference = () => {
      try {
        const stored = localStorage.getItem(STREAMING_PREFERENCE_KEY);
        if (stored) {
          const preference: StreamingPreference = JSON.parse(stored);
          setIsStreamingEnabled(preference.enabled);
        }
      } catch (error) {
        console.warn('Failed to load streaming preference:', error);
        // Use default value (true) if loading fails
      } finally {
        setIsLoading(false);
      }
    };

    loadPreference();
  }, []);

  // Save preference to localStorage
  const savePreference = useCallback((enabled: boolean) => {
    try {
      const preference: StreamingPreference = {
        enabled,
        lastUpdated: Date.now()
      };
      localStorage.setItem(STREAMING_PREFERENCE_KEY, JSON.stringify(preference));
    } catch (error) {
      console.warn('Failed to save streaming preference:', error);
    }
  }, []);

  // Toggle streaming preference
  const toggleStreaming = useCallback((enabled?: boolean) => {
    const newValue = enabled !== undefined ? enabled : !isStreamingEnabled;
    setIsStreamingEnabled(newValue);
    savePreference(newValue);
  }, [isStreamingEnabled, savePreference]);

  // Reset to default
  const resetToDefault = useCallback(() => {
    setIsStreamingEnabled(true);
    savePreference(true);
  }, [savePreference]);

  // Clear stored preference
  const clearPreference = useCallback(() => {
    try {
      localStorage.removeItem(STREAMING_PREFERENCE_KEY);
      setIsStreamingEnabled(true); // Reset to default
    } catch (error) {
      console.warn('Failed to clear streaming preference:', error);
    }
  }, []);

  return {
    isStreamingEnabled,
    isLoading,
    toggleStreaming,
    resetToDefault,
    clearPreference
  };
}; 